import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { LOCALES } from "./locales";

export const SUPPORTED = ["en", "pt", "es", "it"];
export const LANGUAGE_NAMES = { en: "English", pt: "Português", es: "Español", it: "Italiano" };
export const LANGUAGE_FLAGS = { en: "🇺🇸", pt: "🇧🇷", es: "🇪🇸", it: "🇮🇹" };
const FALLBACK = "pt"; // this deployment is PT-first; unsupported browsers fall back here
const STORAGE_KEY = "secproctoring.lang";

function normalize(lang) {
  const base = (lang || "").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(base) ? base : null;
}

function detectInitial() {
  if (typeof window === "undefined") return FALLBACK;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (normalize(stored)) return normalize(stored);
  const candidates =
    navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const c of candidates) {
    const n = normalize(c);
    if (n) return n;
  }
  return FALLBACK;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectInitial);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang === "pt" ? "pt-BR" : lang;
  }, [lang]);

  const setLanguage = useCallback((value) => {
    setLang(normalize(value) || FALLBACK);
  }, []);

  // t(key, fallbackText, vars) — flat dot-namespaced keys, {var} interpolation.
  const t = useCallback(
    (key, fallbackText, vars) => {
      const table = LOCALES[lang] || LOCALES[FALLBACK];
      // Fallback chain: chosen language -> English (complete) -> literal.
      let str = table[key] ?? LOCALES.en[key] ?? fallbackText ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          str = str.replaceAll(`{${k}}`, String(v));
        }
      }
      return str;
    },
    [lang]
  );

  return (
    <I18nContext.Provider value={{ lang, setLanguage, t, supported: SUPPORTED }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
